//
//  AiAccounts.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: October 26, 2026
//  Author: Bob Keathley
//
//  AI accounts (§6w): everyone sees their own. An owner or security-admin also
//  sees the corporate allowlist and the proposal queue, approves or rejects a
//  proposal with a reason (never on their own accounts), and adds a corporate
//  account directly. A self-declared "corporate" label never counts by itself.
//
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { api } from '@shared/api/client'
import { isOk } from '@shared/api/envelope'
import { aiAccountsPath, canDecideAccounts, decideAccountPath, identityText, myAiAccountsPath, peopleErrorText, providerLabel, removeCorporatePath, type AiAccount, type AiProvider } from '@shared/api/people'
import type { Session } from '@shared/auth'

type Page = { items: AiAccount[]; next_cursor?: string | null }
const when = (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : '—')
const statusPill = (s: AiAccount['status']) =>
  s === 'approved_corporate' ? <span className="pill ok">corporate</span>
    : s === 'proposed_corporate' ? <span className="pill watchp">waiting for approval</span>
      : s === 'rejected' ? <span className="pill off">rejected</span> : <span className="pill">personal</span>

function QueueRow({ a, mine, token, onDone, onError }: { a: AiAccount; mine: boolean; token: string; onDone: (m: string) => void; onError: (m: string) => void }) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  async function decide(verb: 'approve' | 'reject') {
    setBusy(true)
    const r = await api<AiAccount>(decideAccountPath(a.id, verb), { method: 'POST', body: JSON.stringify({ reason: reason.trim() }) }, token)
    setBusy(false)
    if (!isOk(r)) { onError(peopleErrorText(r.error)); return }
    onDone(`${verb === 'approve' ? 'Approved' : 'Rejected'} ${a.account_label}.`)
  }
  return (
    <tr>
      <td>{a.owner ? <>{a.owner.name || a.owner.email}<div className="hint">{a.owner.email}</div></> : <span className="hint">—</span>}</td>
      <td>{providerLabel(a.provider)}<div className="hint">{a.account_label}</div></td>
      <td className="hint">{identityText(a.account_identity) || '—'}</td>
      <td className="hint">{when(a.created_at)}</td>
      <td style={{ textAlign: 'right' }}>
        {mine
          ? <span className="hint">Your own account: another admin decides.</span>
          : (
            <span className="row">
              <input aria-label={`Reason for ${a.account_label}`} placeholder="Reason (required)" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
              <button className="btn sm fixed" disabled={busy || !reason.trim()} onClick={() => decide('approve')}>Approve</button>
              <button className="btn danger sm fixed" disabled={busy || !reason.trim()} onClick={() => decide('reject')}>Reject</button>
            </span>
          )}
      </td>
    </tr>
  )
}

function AllowRow({ a, mine, token, onDone, onError }: { a: AiAccount; mine: boolean; token: string; onDone: (m: string) => void; onError: (m: string) => void }) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  async function remove() {
    setBusy(true)
    const r = await api<{ removed: boolean }>(removeCorporatePath(a.id), { method: 'DELETE' }, token)
    setBusy(false); setConfirming(false)
    if (!isOk(r)) { onError(peopleErrorText(r.error)); return }
    onDone(`Removed ${a.account_label} from the corporate allowlist.`)
  }
  return (
    <tr>
      <td>{providerLabel(a.provider)}<div className="hint">{a.account_label}</div></td>
      <td className="hint">{identityText(a.account_identity) || '—'}</td>
      <td className="hint">{a.owner ? a.owner.name || a.owner.email : 'tenant'}</td>
      <td className="hint">{when(a.decided_at)}</td>
      <td className="hint">{a.reason ?? '—'}</td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {mine ? <span className="hint">Your own: another admin removes it.</span>
          : confirming
            ? <>Remove from the allowlist? <button className="btn danger sm" disabled={busy} onClick={remove}>{busy ? 'Removing…' : 'Yes, remove'}</button>{' '}<button className="btn ghost sm" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button></>
            : <button className="btn danger sm" onClick={() => setConfirming(true)}>Remove</button>}
      </td>
    </tr>
  )
}

export default function AiAccounts({ session }: { session: Session }) {
  const token = session.token
  const admin = canDecideAccounts(session.user.role)
  const [mine, setMine] = useState<AiAccount[] | null>(null)
  const [queue, setQueue] = useState<Page | null>(null)
  const [allow, setAllow] = useState<Page | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [provider, setProvider] = useState<AiProvider>('anthropic')
  const [label, setLabel] = useState('')
  const [email, setEmail] = useState('')
  const [org, setOrg] = useState('')
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const [m, q, a] = await Promise.all([
      api<{ items: AiAccount[] }>(myAiAccountsPath, {}, token),
      admin ? api<Page>(aiAccountsPath('proposed_corporate'), {}, token) : Promise.resolve(null),
      admin ? api<Page>(aiAccountsPath('approved_corporate'), {}, token) : Promise.resolve(null),
    ])
    const failed = [m, q, a].find((r) => r !== null && !isOk(r))
    setLoadErr(failed && !isOk(failed) ? peopleErrorText(failed.error) : null)
    setMine(isOk(m) ? m.data.items : [])
    setQueue(q && isOk(q) ? q.data : null)
    setAllow(a && isOk(a) ? a.data : null)
  }, [token, admin])
  useEffect(() => { load() }, [load])

  const done = (msg: string) => { setError(null); setNotice(msg); load() }

  async function addCorporate(e: FormEvent) {
    e.preventDefault()
    setBusy(true); setError(null); setNotice(null)
    const identity: Record<string, string> = {}
    if (email.trim()) identity.email = email.trim()
    if (org.trim()) identity.organization = org.trim()
    const r = await api<AiAccount>('/ai-accounts', { method: 'POST', body: JSON.stringify({ provider, account_label: label.trim(), account_identity: identity, reason: why.trim() }) }, token)
    setBusy(false)
    if (!isOk(r)) { setError(peopleErrorText(r.error)); return }
    setLabel(''); setEmail(''); setOrg(''); setWhy('')
    done(`Added ${r.data.account_label} to the corporate allowlist.`)
  }

  if (mine === null) return <div className="hierarchy"><p className="hint">Loading…</p></div>

  return (
    <div className="hierarchy">
      <h2>AI accounts</h2>
      <p className="hint">The AI accounts people use, personal and corporate. A person adds theirs in the Signet app on their Mac. A corporate account counts as corporate only once an owner or security admin approves it.</p>
      {loadErr && <p className="err" role="alert">{loadErr}</p>}
      {error && <p className="err" role="alert">{error}</p>}
      {notice && <p className="hint" role="status">{notice}</p>}

      {admin && (
        <>
          <div className="panel">
            <h3 style={{ marginTop: 0 }}>Waiting for approval</h3>
            <table>
              <thead><tr><th>Person</th><th>Account</th><th>Identity</th><th>Proposed</th><th></th></tr></thead>
              <tbody>
                {queue === null && <tr><td colSpan={5} className="hint">The queue could not be loaded.</td></tr>}
                {queue && queue.items.length === 0 && <tr><td colSpan={5} className="hint">Nothing is waiting.</td></tr>}
                {queue?.items.map((a) => <QueueRow key={a.id} a={a} mine={a.user_id === session.user.id} token={token} onDone={done} onError={setError} />)}
              </tbody>
            </table>
            {queue?.next_cursor && <p className="hint">More are waiting than shown. Decide these, then reload.</p>}
          </div>

          <div className="panel">
            <h3 style={{ marginTop: 0 }}>Corporate allowlist</h3>
            <table>
              <thead><tr><th>Account</th><th>Identity</th><th>Person</th><th>Approved</th><th>Reason</th><th></th></tr></thead>
              <tbody>
                {allow === null && <tr><td colSpan={6} className="hint">The allowlist could not be loaded.</td></tr>}
                {allow && allow.items.length === 0 && <tr><td colSpan={6} className="hint">No corporate accounts yet.</td></tr>}
                {allow?.items.map((a) => <AllowRow key={a.id} a={a} mine={a.user_id === session.user.id} token={token} onDone={done} onError={setError} />)}
              </tbody>
            </table>
          </div>

          <form className="panel" onSubmit={addCorporate}>
            <h3 style={{ marginTop: 0 }}>Add corporate account</h3>
            <p className="hint">Goes straight onto the allowlist. Enter the email or organization the provider reports, never a token or key.</p>
            <div className="row">
              <select aria-label="Provider" value={provider} onChange={(e) => setProvider(e.target.value as AiProvider)}>
                <option value="anthropic">Anthropic</option><option value="openai">OpenAI</option><option value="google">Google</option><option value="other">Other</option>
              </select>
              <input aria-label="Account label" placeholder="Label, for example Acme Claude" maxLength={80} value={label} onChange={(e) => setLabel(e.target.value)} />
              <input aria-label="Account email" placeholder="Account email" maxLength={200} value={email} onChange={(e) => setEmail(e.target.value)} />
              <input aria-label="Organization" placeholder="Organization" maxLength={200} value={org} onChange={(e) => setOrg(e.target.value)} />
              <input aria-label="Reason" placeholder="Reason (required)" maxLength={500} value={why} onChange={(e) => setWhy(e.target.value)} />
              <button className="btn fixed" disabled={busy || !label.trim() || !why.trim() || (!email.trim() && !org.trim())}>{busy ? 'Adding…' : 'Add corporate account'}</button>
            </div>
          </form>
        </>
      )}

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Your accounts</h3>
        <table>
          <thead><tr><th>Account</th><th>Identity</th><th>Status</th><th>Decision</th></tr></thead>
          <tbody>
            {mine.length === 0 && <tr><td colSpan={4} className="hint">None yet. Add them in the Signet app on your Mac.</td></tr>}
            {mine.map((a) => (
              <tr key={a.id}>
                <td>{providerLabel(a.provider)}<div className="hint">{a.account_label}</div></td>
                <td className="hint">{identityText(a.account_identity) || '—'}</td>
                <td>{statusPill(a.status)}</td>
                <td className="hint">{a.decided_at ? <>{when(a.decided_at)}{a.reason ? ` · ${a.reason}` : ''}</> : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
